// PROVISION-IN-PRODUCT (2026-10-30; docs/design htva-only audit P2): whatever ops scripts did by
// hand for one company, the product does for every company.
//
//   - Settings (founder rule: data a platform super admin edits, never constants or files): the
//     DE set-up defaults, budget rollover, feature rules, the wake intake and the system agent, as
//     a platform default and optionally one company's override. Operator routes, platform super
//     admins only, each change audited in `cp.platform_audit` (the console page is OPERATOR-CONSOLE).
//   - A company's own steps (its system agent, its feature rules) run in the `provision.companies`
//     sweep; a super admin can preview them or ask for a run.
//   - A DE's set-up (budget, MCP agent key, Recall memory through the key, General space access,
//     runtime placement, the Chief of Staff digest, a first task) runs when the DE is created; a
//     manager sees each step on the DE and can run the set-up again.
//   - The satellite (AiGentPanel / ClawPanel) claims a DE's keys and a system agent's key over its
//     signed channel, and registers a DE's bridge (wake URL + secret) with a signed callback —
//     the intake that replaces the hand-written `bridge-wake.json`.
import { z } from "zod";
import {
  AddonKey,
  AgentKeyScope,
  ProvisioningSettingKey,
  ProvisioningStepState,
  SatelliteReleaseAction,
  SatelliteReleaseComponent,
  SkillsmithActionKind,
  SystemAgentKeyScope,
} from "../enums";
import { BoundedJson, Timestamp, TimeZone, Uuid } from "../primitives";
import { CompanyItemParams } from "./common";
import { defineRoute } from "./route";

const op = "/v1/operator";
const operator = { feature: "core", tag: "operator", access: "operator" } as const;
const company = { feature: "core", tag: "digital-employees" } as const;
const panel = { feature: "core", tag: "operator", access: "public" } as const;

// Setting values ---------------------------------------------------------------------------------

const Name = z.string().trim().min(1).max(120);
const LongText = z.string().trim().min(1).max(4000);
const Threshold = z.string().regex(/^0?\.\d{1,3}$|^1(\.0{1,3})?$/, "a share between 0 and 1");
/** A scope the DE's MCP agent key carries: the agent key's own enum (Codex: no free text). */
const KeyScope = AgentKeyScope;

/** `de_defaults`: what creating a DE sets up (each part can be switched off with null/false). */
export const DeDefaultsSetting = z
  .strictObject({
    /** Off: a new DE gets nothing set up (a manager can still run the set-up by hand). */
    enabled: z.boolean(),
    /** The DE's own monthly budget (in the company's reporting currency); null: none. */
    budget: z
      .strictObject({
        amount_minor: z.int().min(0).max(100_000_000),
        /** The amount is also its hard cap (runs stop there). */
        hard_cap: z.boolean(),
        soft_thresholds: z.array(Threshold).max(5),
        alert_only: z.boolean(),
      })
      .nullable(),
    /** The DE's CompanyOS MCP agent key; null: no key (and so no Recall memory key either). */
    key_scopes: z.array(KeyScope).min(1).max(20).nullable(),
    /** The DE's relation on the company's General space; null: none. */
    general_space: z.enum(["editor", "viewer"]).nullable(),
    /** `self_hosted`: a lead profile marked self-hosted (the satellite runs it); `none`: left to deploy. */
    runtime: z.enum(["none", "self_hosted"]),
    /** Role templates whose DE gets the Chief of Staff digest switched on. */
    cos_digest_template_keys: z.array(z.string().min(1).max(64)).max(20),
    /** A first check-in task on this board of the General space (made if missing); null: none. */
    first_task: z.strictObject({ board_name: Name, title: Name, body: LongText }).nullable(),
  })
  .meta({ id: "DeDefaultsSetting" });

/** `budget_rollover`: budgets carry into the next month unless changed. */
export const BudgetRolloverSetting = z
  .strictObject({
    /** Off: the `budget.rollover` job starts no new period for this company. */
    enabled: z.boolean(),
    /** Whether a new budget repeats monthly when its request does not say. */
    repeat_monthly_default: z.boolean(),
  })
  .meta({ id: "BudgetRolloverSetting" });

/** What switches a feature on: the plan, an integration, an installed skill, a live DE, always. */
export const FeatureTrigger = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("always") }),
    z.strictObject({
      kind: z.literal("plan"),
      /** A plan key, or `*`: any live subscription. */
      key: z.string().min(1).max(64),
    }),
    z.strictObject({ kind: z.literal("integration"), provider: z.string().min(1).max(64) }),
    z.strictObject({ kind: z.literal("skill"), key: z.string().min(1).max(64) }),
    z.strictObject({ kind: z.literal("de") }),
  ])
  .meta({ id: "FeatureTrigger" });
export type FeatureTrigger = z.infer<typeof FeatureTrigger>;

/**
 * `feature_rules`: features switch on by plan, skills and integrations, never by hand-set
 * per-company toggles. A rule switches its feature on (source `rule`) when any trigger holds and
 * off again when none does; an admin's own entitlement row (any other source) always wins.
 */
export const FeatureRulesSetting = z
  .strictObject({
    /** On: the sweep re-applies the rules to provisioned companies every hour. */
    auto_apply: z.boolean(),
    rules: z
      .array(z.strictObject({ feature: AddonKey, when: z.array(FeatureTrigger).min(1).max(20) }))
      .max(50),
  })
  .meta({ id: "FeatureRulesSetting" });

/** `wake_intake`: what a satellite may register as a DE's bridge. */
export const WakeIntakeSetting = z
  .strictObject({
    /** Off: registrations are refused (and the stored ones are not pushed to). */
    enabled: z.boolean(),
    /** Only overlay (WireGuard) addresses, even over https. */
    overlay_only: z.boolean(),
  })
  .meta({ id: "WakeIntakeSetting" });

/** `system_agent`: every company's own hidden system agent. */
export const SystemAgentSetting = z
  .strictObject({
    /** Off: new companies get none. */
    enabled: z.boolean(),
    /** Its name inside the company (never shown to the company's people). */
    display_name: Name,
    /** The scopes of the key the satellite claims for it. */
    key_scopes: z.array(SystemAgentKeyScope).min(1),
  })
  .meta({ id: "SystemAgentSetting" });

/** A path inside the Hermes container (absolute, no dot segments). */
const ContainerPath = z
  .string()
  .max(200)
  .regex(/^\/(?:[A-Za-z0-9_.-]+\/)*[A-Za-z0-9_.-]+$/)
  .refine((p) => !p.split("/").some((s) => s === "." || s === ".."), "no dot segments");
/** A model name as the LiteLLM gateway knows it. */
const ModelName = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9._:/-]+$/);

/**
 * SATELLITE-RUNTIME `bridge_runtime`: how the wake bridge runs a self-hosted DE — the same for
 * every company unless a super admin overrides one company. Replaces the per-company unit env
 * (BRIDGE_POLL_SECONDS, BRIDGE_RUN_BINDING, BRIDGE_GATEWAY_DES, BRIDGE_KANBAN_DES, the quick-reply
 * model, …); the satellite runtime fetch serves it with each company's roster.
 */
export const BridgeRuntimeSetting = z
  .strictObject({
    /** The fallback inbox poll (a push wakes the DE at once). */
    poll_seconds: z.int().min(5).max(600),
    heartbeat_seconds: z.int().min(5).max(55),
    lease_seconds: z.int().min(30).max(600),
    /** A run is stopped after this long. */
    run_timeout_seconds: z.int().min(60).max(7200),
    /** How a call binds to the run: the DE's key (`key`), the arguments (`args`), a header. */
    run_binding: z.enum(["key", "args", "header"]),
    /** `gateway`: the container's Hermes gateway (mid-run messages are steered in); `chat`: one-shot. */
    run_via: z.enum(["chat", "gateway"]),
    steer_poll_seconds: z.int().min(2).max(120),
    /** Helper (kanban) cards a DE created wake it when they finish, block or crash. */
    kanban_wake: z.strictObject({
      enabled: z.boolean(),
      max_wakes_per_hour: z.int().min(1).max(120),
      min_gap_seconds: z.int().min(0).max(3600),
    }),
    // No quick-reply model or key here: CompanyOS generates the instant first reply with the
    // company's own model connection (wp/QUICK-REPLY-COMPANY-KEY); the bridge only asks for it.
    /** Where a message's files land in the container, and where the DE drops files to send. */
    files: z.strictObject({ inbox_dir: ContainerPath, outbox_dir: ContainerPath }),
    /** The model a run's usage is booked to when Hermes does not name it (null: the DE's own). */
    fallback_model: ModelName.nullable(),
  })
  .meta({ id: "BridgeRuntimeSetting" });
export type BridgeRuntimeSetting = z.infer<typeof BridgeRuntimeSetting>;

/** SATELLITE-RUNTIME: a release a satellite installs (the artifact is checked by its sha256). */
export const ReleasePin = z
  .strictObject({
    version: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[A-Za-z0-9._+-]+$/),
    /**
     * Where the panel gets it, in one of these forms (the bytes are always checked against
     * `sha256`; there is no signature yet):
     *   `https://…`                          a file to download (the bridge);
     *   `vendored:<repo>@<commit>:<path>`    a file the panel vendors from a pinned commit;
     *   `build:<contextTag>`                 an image the panel builds on the node from that
     *                                        content tag (render service, until a registry exists;
     *                                        `sha256` is the image id);
     *   `<registry>/<image>@sha256:<hex>`    an image pulled by digest.
     */
    artifact: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .regex(
        /^(https:\/\/\S+|vendored:[\w.-]+@[0-9a-f]{7,40}:[\w./-]+|build:[0-9a-f]{12,64}|[\w.-]+(:\d+)?\/[\w./-]+@sha256:[0-9a-f]{64})$/,
        "https URL, vendored:<repo>@<commit>:<path>, build:<contextTag> or <registry>/<image>@sha256:<digest>",
      ),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    notes: z.string().trim().max(500).optional(),
  })
  .meta({ id: "ReleasePin" });
export type ReleasePin = z.infer<typeof ReleasePin>;

/**
 * SATELLITE-RUNTIME `satellite_runtime` (platform only: one value for every satellite): the wake
 * listener's port on the overlay, how often the panel refreshes a satellite's runtime, and the
 * pinned releases. Rolling back is pinning the earlier release again (each change is audited).
 */
export const SatelliteRuntimeSetting = z
  .strictObject({
    wake_port: z.int().min(1024).max(65535),
    config_poll_seconds: z.int().min(30).max(3600),
    releases: z.strictObject({
      bridge: ReleasePin.nullable(),
      render_service: ReleasePin.nullable(),
    }),
  })
  .meta({ id: "SatelliteRuntimeSetting" });
export type SatelliteRuntimeSetting = z.infer<typeof SatelliteRuntimeSetting>;

/** A line switch's name as the company's line config reads it (`video_line`, `design_line`, …). */
export const LineSwitchKey = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);

/**
 * SATELLITE-RUNTIME `line_switches` (audit C36, C53, C128, C147): the switches an operator once
 * set by editing a container's `line.local.yaml` ("NOT ENABLED"), as rules: a switch is on for a
 * company while any of its triggers holds — a skill installed, an integration connected, a plan,
 * always. Never a per-company hand toggle; a company override replaces the rules for that company.
 */
export const LineSwitchesSetting = z
  .strictObject({
    rules: z
      .array(
        z.strictObject({
          switch: LineSwitchKey,
          when: z.array(FeatureTrigger).min(1).max(20),
          note: z.string().trim().max(200).optional(),
        }),
      )
      .max(50),
  })
  .meta({ id: "LineSwitchesSetting" });
export type LineSwitchesSetting = z.infer<typeof LineSwitchesSetting>;

/**
 * `memory_lane` (RECALL-INBOX-LOOP): days a Memory lane card waits with its department before it
 * also goes to the owner/CEO. The platform default is 3; a company may override it.
 */
export const MemoryLaneSetting = z
  .strictObject({ escalation_days: z.int().min(1).max(30) })
  .meta({ id: "MemoryLaneSetting" });
export type MemoryLaneSetting = z.infer<typeof MemoryLaneSetting>;

/**
 * NEW-CLIENT-PROCEDURE: the company may have a Recall tenant made on a server whose Recall is
 * shared with other tenants (`RECALL_TENANT_ONLY` set), in place of listing its slug in the env.
 * `tenant_name` replaces the company slug in the tenant's name (the env's `slug=name`).
 */
export const RecallTenantSetting = z
  .strictObject({
    enabled: z.boolean(),
    tenant_name: z
      .string()
      .max(63)
      .regex(/^[a-z0-9][a-z0-9-]{0,62}$/)
      .nullable(),
  })
  .meta({ id: "RecallTenantSetting" });
export type RecallTenantSetting = z.infer<typeof RecallTenantSetting>;

// OPERATOR-CONSOLE (fleet tuning loop §2.8): what was set by hand in files, env and ops scripts.

const Effort = z.enum(["off", "low", "medium", "high"]);
const RuntimeProfileName = z.enum(["light", "standard", "heavy"]);
/** A role kind as templates name it (`sdr`, `chief-of-staff`, `default`, …). */
const RoleKind = z.string().regex(/^[a-z][a-z0-9_-]{0,47}$/);
const ContainerName = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_.-]+$/);
const VersionLabel = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9._+-]+$/);

/**
 * `models` (was: each profile's `config.yaml` by `models-apply.py`, `AGENT_AUX_MODEL`, `line.yaml`):
 * the model, effort and vision per role kind, and the auxiliary vision model. A company override
 * replaces the list for that company. An empty list: the effort defaults decide.
 */
export const ModelsSetting = z
  .strictObject({
    kinds: z
      .array(
        z.strictObject({ kind: RoleKind, model: ModelName, effort: Effort, vision: z.boolean() }),
      )
      .max(50),
    aux_vision_model: ModelName.nullable(),
  })
  .meta({ id: "ModelsSetting" });
export type ModelsSetting = z.infer<typeof ModelsSetting>;

const RuntimeProfile = z.strictObject({
  /** Concurrent Hermes sessions in the container. */
  max_sessions: z.int().min(1).max(64),
  /** Concurrent runs the bridge starts. */
  max_runs: z.int().min(1).max(64),
  /** `kanban.max_in_progress`: helper cards in progress at once. */
  kanban_max_in_progress: z.int().min(1).max(64),
  /** The dispatcher's interval. */
  dispatch_interval_seconds: z.int().min(5).max(3600),
});

/**
 * `runtime_profiles` (was: `hermes-baseline.js` constants, root config by hand): concurrency and
 * dispatch per profile, as ClawPanel's baseline has them. A satellite override replaces all of it
 * for that satellite. The children cap is the company's (`delegation.max_concurrent_children`),
 * and the container's memory is provisioning's (`container_memory`).
 */
export const RuntimeProfilesSetting = z
  .strictObject({ light: RuntimeProfile, standard: RuntimeProfile, heavy: RuntimeProfile })
  .meta({ id: "RuntimeProfilesSetting" });
export type RuntimeProfilesSetting = z.infer<typeof RuntimeProfilesSetting>;

/**
 * `container_memory`: the container memory limit (MB) per profile, the provisioning default for a
 * container of that profile. Not part of the ClawPanel baseline; it belongs to provisioning.
 */
export const ContainerMemorySetting = z
  .strictObject({
    light: z.int().min(256).max(262_144),
    standard: z.int().min(256).max(262_144),
    heavy: z.int().min(256).max(262_144),
  })
  .meta({ id: "ContainerMemorySetting" });
export type ContainerMemorySetting = z.infer<typeof ContainerMemorySetting>;

/**
 * `baseline` (was: `BASELINE_VERSION` in ClawPanel's code, `.clawpanel-baseline.json` in each
 * container): the ClawPanel baseline version and memory profile, platform-wide, per company or per
 * satellite, and per container on a satellite. ClawPanel renders the container from these rows.
 */
export const BaselineSetting = z
  .strictObject({
    /** Null: the baseline ClawPanel ships. */
    version: VersionLabel.nullable(),
    profile: RuntimeProfileName,
    /** Per container (satellite scope only; empty elsewhere). */
    containers: z
      .array(
        z.strictObject({
          container: ContainerName,
          version: VersionLabel.nullable(),
          profile: RuntimeProfileName.nullable(),
        }),
      )
      .max(200),
  })
  .meta({ id: "BaselineSetting" });
export type BaselineSetting = z.infer<typeof BaselineSetting>;

/**
 * `roster_overrides` (company; was: `htva-workforce.json`, `hermes.json`): the roster is generated
 * from the registry and the memberships; these are the per-DE exceptions (placement, model).
 */
export const RosterOverridesSetting = z
  .strictObject({
    des: z
      .array(
        z.strictObject({
          membership_id: Uuid,
          satellite_id: Uuid.nullable(),
          container: ContainerName.nullable(),
          profile: ContainerName.nullable(),
          model: ModelName.nullable(),
          runtime_profile: RuntimeProfileName.nullable(),
        }),
      )
      .max(500),
  })
  .meta({ id: "RosterOverridesSetting" });
export type RosterOverridesSetting = z.infer<typeof RosterOverridesSetting>;

/**
 * `base_prompt` (was: code in `packages/domain-agents`, copies in the bridge): which base prompt
 * version is served with the wake; null: the version the code ships. A company may pin one.
 */
export const BasePromptSetting = z
  .strictObject({ version: VersionLabel.nullable() })
  .meta({ id: "BasePromptSetting" });
export type BasePromptSetting = z.infer<typeof BasePromptSetting>;

/**
 * `skills_defaults` (was: the seed's `publishPlatformPacks`, `platformSkills` in the baseline): the
 * library packs every company starts with, their pinned versions, and the feature each switches on.
 * An empty list: the seed's platform packs.
 */
export const SkillsDefaultsSetting = z
  .strictObject({
    packs: z
      .array(
        z.strictObject({
          key: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
          /** Null: the newest published version. */
          version: VersionLabel.nullable(),
          /** Installed in every company. */
          core: z.boolean(),
          /** The feature it switches on (a `feature_rules` skill trigger), if any. */
          gated_feature: z
            .string()
            .regex(/^[a-z][a-z0-9_]{0,63}$/)
            .nullable(),
        }),
      )
      .max(100),
  })
  .meta({ id: "SkillsDefaultsSetting" });
export type SkillsDefaultsSetting = z.infer<typeof SkillsDefaultsSetting>;

/**
 * `rollout` (fleet tuning loop §2.4): the canary and the rollout rules. The canary is always the
 * platform operator's home company first (HTVA) unless a super admin names another.
 */
export const RolloutSetting = z
  .strictObject({
    /** Null: the platform operator's home company. */
    canary_company_id: Uuid.nullable(),
    /** One volunteer client after the canary, if any. */
    volunteer_company_id: Uuid.nullable(),
    window_hours: z.int().min(1).max(720),
    window_runs: z.int().min(1).max(100_000),
    /** Auto-stop when a company's issue rate passes its own baseline × this. */
    auto_stop_factor: z.number().min(1).max(10),
    /** Companies in rollout order after the canary (the rest follow). */
    wave_order: z.array(Uuid).max(500),
  })
  .meta({ id: "RolloutSetting" });
export type RolloutSetting = z.infer<typeof RolloutSetting>;

/**
 * `limits` (was: `BUDGET_CENTS` in ops scripts, the system agent's `daily_cap_micros` default,
 * finance controls by hand): the platform defaults; a company may override them. The DE budget
 * itself is `de_defaults.budget`.
 */
export const LimitsSetting = z
  .strictObject({
    /** The company's monthly envelope across its DEs (minor units); null: none. */
    company_envelope_minor: z.int().min(0).max(1_000_000_000).nullable(),
    /** Runs stop at the envelope (else an alert only). */
    hard_cap: z.boolean(),
    /** The system agent's (Skillsmith's) daily model spend cap. */
    system_agent_daily_cap_micros: z.int().min(0).max(1_000_000_000),
    /** Each operator agent's daily cap unless its own row sets one. */
    operator_agent_daily_cap_micros: z.int().min(0).max(1_000_000_000),
    /** Money limits for outward actions (fields only until a payment tool exists). */
    money: z.strictObject({
      per_transaction_minor: z.int().min(0).max(1_000_000_000).nullable(),
      per_day_minor: z.int().min(0).max(1_000_000_000).nullable(),
    }),
  })
  .meta({ id: "LimitsSetting" });
export type LimitsSetting = z.infer<typeof LimitsSetting>;

/** A wall-clock time of day, `HH:MM` (company time). */
const WallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "a time of day, HH:MM");

/**
 * NIGHTLY-REVIEW (Andrei, 2026-11-01): every DE reviews its own day each night and files
 * improvement proposals (never a change). The reviews are spread over a window in company time
 * (each DE at a stable offset hashed from its id, so 300 agents never pile up at 02:00), drained
 * at most `concurrency` at a time across the platform, and carried past the window's end rather
 * than dropped. The cost per company per night is capped; a DE files at most `max_proposals`.
 * Off until the worker runs with `NIGHTLY_REVIEW=1` (the flag), whatever these say.
 */
export const NightlyReviewSetting = z
  .strictObject({
    /** The window's start, company time (default 02:00). */
    window_start: WallTime,
    /** The window's end, company time (default 04:00; past it, pending reviews still run). */
    window_end: WallTime,
    /** Reviews running at once across the platform (platform scope only is read). */
    concurrency: z.int().min(1).max(100),
    /** The model spend cap per company per night (micros). */
    cost_cap_micros: z.int().min(0).max(100_000_000),
    /** Proposals one DE may file per night. */
    max_proposals: z.int().min(0).max(3),
    /** The gateway model the review runs on (a light hosted model). */
    model: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .regex(/^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,199}$/, "a gateway model name"),
  })
  .meta({ id: "NightlyReviewSetting" });
export type NightlyReviewSetting = z.infer<typeof NightlyReviewSetting>;

/**
 * FAILURE-STOP (Andrei, 2026-11-02: "stop after 2–3 failures, never 40–50"): when the MCP server
 * stops a run that keeps failing. After `same_refusal_limit` identical refusals (same tool, same
 * error code) the next identical call is refused at once ("stop: repeated refusal"); after
 * `streak_limit` errors in a row or `run_limit` errors in the run, the run is stopped (needs you).
 */
export const FailureStopSetting = z
  .strictObject({
    enabled: z.boolean(),
    same_refusal_limit: z.int().min(1).max(20),
    streak_limit: z.int().min(1).max(50),
    run_limit: z.int().min(1).max(200),
  })
  .meta({ id: "FailureStopSetting" });
export type FailureStopSetting = z.infer<typeof FailureStopSetting>;

/**
 * TASK-BUDGETS (roadmap Phase 1 §3): one task's hard limits — tool steps, cost (micros of the
 * company's reporting currency) and wall time. A missing field leaves the level below in force.
 */
export const TaskLimits = z
  .strictObject({
    steps: z.int().min(1).max(10_000).optional(),
    cost_micros: z.int().min(1).max(1_000_000_000).optional(),
    time_ms: z
      .int()
      .min(60_000)
      .max(24 * 3_600_000)
      .optional(),
  })
  .meta({ id: "TaskLimits" });
export type TaskLimits = z.infer<typeof TaskLimits>;

/**
 * TASK-BUDGETS: the shipped per-task limits for DEs and helpers (platform default, company
 * override; a DE's `digital_employee.task_limits` and an agent profile's `overrides.task_limits`
 * narrow further). `enabled` switches enforcement on; off, runs carry their limits unenforced.
 */
export const TaskBudgetsSetting = z
  .strictObject({
    enabled: z.boolean(),
    de: TaskLimits.required(),
    helper: TaskLimits.required(),
  })
  .meta({ id: "TaskBudgetsSetting" });
export type TaskBudgetsSetting = z.infer<typeof TaskBudgetsSetting>;

/**
 * TASK-BUDGETS (core plan item 8): one DE's per-task limits as its seat settings show them —
 * whether the company enforces them, the company's DE defaults (shipped → platform → company),
 * the DE's own override (`digital_employee.task_limits`, null: none) and the limits a new run of
 * the DE gets (defaults with the override's set fields; an agent profile may narrow it further).
 */
export const DeTaskLimitsView = z
  .strictObject({
    membership_id: Uuid,
    enabled: z.boolean(),
    defaults: TaskLimits.required(),
    override: TaskLimits.nullable(),
    effective: TaskLimits.required(),
  })
  .meta({ id: "DeTaskLimitsView" });
export type DeTaskLimitsView = z.infer<typeof DeTaskLimitsView>;

/** TASK-BUDGETS: set the DE's override (only the fields to differ), or null to clear it. */
export const DeTaskLimitsPut = z
  .strictObject({ override: TaskLimits.nullable() })
  .meta({ id: "DeTaskLimitsPut" });
export type DeTaskLimitsPut = z.infer<typeof DeTaskLimitsPut>;

/**
 * SKILLSMITH-LENS (design 2026-11-02 §2, §3): the super admin's Skillsmith. Three scheduled
 * reports in the operator's time zone (daily alerts digest, weekly fleet on Monday, monthly skill
 * performance on the 1st), which of them are mailed (default: weekly and monthly only; the daily
 * digest stays in the Skillsmith space), the model (`deepseek-flash` only: agents never run Claude),
 * the chat's caps, and the one-click actions allowed (this list may shrink the set, never grow it).
 * Off until the API and the worker run with `SKILLSMITH_LENS=1`, whatever these say.
 *
 * SKILLSMITH-CONTROL: three of these wait on Andrei's decision (design §7) and so default to the
 * most conservative value until he decides: `mail` (decision 1: every report stays in the space
 * and the inbox, none mailed), `allowed_actions` (decision 2: "Resume a DE" spends the client's
 * budget, so it is not in the one-click set; it stays a proposal), and `think_harder` (decision 3:
 * no re-ask on another model; nothing reads it yet). `standing_orders_per_company` caps the
 * standing orders one client's Skillsmith holds.
 */
export const SkillsmithSetting = z
  .strictObject({
    time_zone: TimeZone,
    daily_at: WallTime,
    weekly_at: WallTime,
    monthly_at: WallTime,
    mail: z.strictObject({ daily: z.boolean(), weekly: z.boolean(), monthly: z.boolean() }),
    model: z.literal("deepseek-flash"),
    /** PENDING (design §7.3): a "think harder" re-ask on another model. Off; not wired. */
    think_harder: z.boolean().default(false),
    /** SKILLSMITH-CONTROL: live (active or paused) standing orders per client. */
    standing_orders_per_company: z.int().min(0).max(50).default(10),
    /** Questions and orders a super admin may give per UTC day (all companies). */
    questions_per_day: z.int().min(0).max(500),
    /** Tool calls one answer may make. */
    max_tool_calls: z.int().min(1).max(8),
    /** The same question to the same company within this many minutes is answered from cache. */
    cache_minutes: z.int().min(0).max(60),
    allowed_actions: z.array(SkillsmithActionKind).max(16),
  })
  .meta({ id: "SkillsmithSetting" });
export type SkillsmithSetting = z.infer<typeof SkillsmithSetting>;

/**
 * CANVAS-CORE: the agentic Live canvas (`/reports/live`: blocks an agent's `show_user` places,
 * the live step stream). Off by default; a super admin switches a company on.
 */
export const LiveCanvasSetting = z
  .strictObject({ enabled: z.boolean() })
  .meta({ id: "LiveCanvasSetting" });
export type LiveCanvasSetting = z.infer<typeof LiveCanvasSetting>;

export const ProvisioningSettingValues = {
  de_defaults: DeDefaultsSetting,
  budget_rollover: BudgetRolloverSetting,
  feature_rules: FeatureRulesSetting,
  wake_intake: WakeIntakeSetting,
  system_agent: SystemAgentSetting,
  bridge_runtime: BridgeRuntimeSetting,
  satellite_runtime: SatelliteRuntimeSetting,
  line_switches: LineSwitchesSetting,
  memory_lane: MemoryLaneSetting,
  models: ModelsSetting,
  runtime_profiles: RuntimeProfilesSetting,
  container_memory: ContainerMemorySetting,
  baseline: BaselineSetting,
  roster_overrides: RosterOverridesSetting,
  base_prompt: BasePromptSetting,
  skills_defaults: SkillsDefaultsSetting,
  rollout: RolloutSetting,
  limits: LimitsSetting,
  nightly_review: NightlyReviewSetting,
  failure_stop: FailureStopSetting,
  skillsmith: SkillsmithSetting,
  recall_tenant: RecallTenantSetting,
  live_canvas: LiveCanvasSetting,
  task_budgets: TaskBudgetsSetting,
} as const;

/** Keys that hold one platform value only (a company override is refused). */
export const PLATFORM_ONLY_SETTINGS = [
  "satellite_runtime",
  "skills_defaults",
  "rollout",
  "skillsmith",
] as const;

/**
 * OPERATOR-CONSOLE: the scopes each key takes. Platform always; a company override where the
 * setting differs by company; a satellite override for what is a satellite's (wake, runtime
 * profiles, baseline).
 */
export const SETTING_SCOPES: Record<
  keyof typeof ProvisioningSettingValues,
  readonly ("platform" | "company" | "satellite")[]
> = {
  de_defaults: ["platform", "company"],
  budget_rollover: ["platform", "company"],
  feature_rules: ["platform", "company"],
  wake_intake: ["platform", "company"],
  system_agent: ["platform", "company"],
  bridge_runtime: ["platform", "company", "satellite"],
  satellite_runtime: ["platform"],
  line_switches: ["platform", "company"],
  memory_lane: ["platform", "company"],
  models: ["platform", "company"],
  runtime_profiles: ["platform", "satellite"],
  container_memory: ["platform", "company"],
  baseline: ["platform", "company", "satellite"],
  roster_overrides: ["company"],
  base_prompt: ["platform", "company"],
  skills_defaults: ["platform"],
  rollout: ["platform"],
  limits: ["platform", "company"],
  nightly_review: ["platform", "company"],
  failure_stop: ["platform", "company"],
  skillsmith: ["platform"],
  recall_tenant: ["company"],
  live_canvas: ["platform", "company"],
  task_budgets: ["platform", "company"],
};
export type ProvisioningKey = keyof typeof ProvisioningSettingValues;
export type ProvisioningSettings = {
  [K in ProvisioningKey]: z.infer<(typeof ProvisioningSettingValues)[K]>;
};

// Request bounds (finding #6): a setting's value is a bounded JSON object.
const Value = BoundedJson;

export const ProvisioningSettingView = z
  .object({
    key: ProvisioningSettingKey,
    /** The shipped default (used while nothing is stored). */
    default: Value,
    /** The stored platform default, null when none. */
    platform: Value.nullable(),
    /** With `company_id`: that company's override, null when none. */
    company: Value.nullable(),
    /** What applies (company override, else platform default, else the shipped default). */
    effective: Value,
    source: z.enum(["company", "platform", "default"]),
    updated_at: Timestamp.nullable(),
  })
  .meta({ id: "ProvisioningSettingView" });

export const ProvisioningSettingList = z
  .object({ company_id: Uuid.nullable(), settings: z.array(ProvisioningSettingView) })
  .meta({ id: "ProvisioningSettingList" });

export const ProvisioningSettingsQuery = z.object({ company_id: Uuid.optional() });
export const ProvisioningSettingParams = z.object({ key: ProvisioningSettingKey });

/** Store a value (validated against the key's shape) or clear it (`value: null`). */
export const ProvisioningSettingPut = z
  .strictObject({
    /** Null or absent: the platform default; else that company's override. */
    company_id: Uuid.nullable().optional(),
    value: BoundedJson.nullable(),
  })
  .meta({ id: "ProvisioningSettingPut" });

// Company provisioning ---------------------------------------------------------------------------

export const ProvisioningCompanyParams = z.object({ company_id: Uuid });

export const CompanyProvisionRequest = z
  .strictObject({
    /** True: only show what a run would do; nothing is queued. */
    dry_run: z.boolean().optional(),
  })
  .meta({ id: "CompanyProvisionRequest" });

export const CompanyProvisionView = z
  .object({
    company_id: Uuid,
    origin: z.enum(["new", "existing"]),
    system_agent_at: Timestamp.nullable(),
    features_at: Timestamp.nullable(),
    requested_at: Timestamp.nullable(),
    /** What a run does now (a dry run) or did last. */
    steps: z.array(z.string()),
    /** Whether a run was queued by this call (the sweep picks it up within a minute). */
    queued: z.boolean(),
  })
  .meta({ id: "CompanyProvisionView" });

// DE set-up --------------------------------------------------------------------------------------

export const DeProvisioningStepName = z.enum([
  "budget",
  "agent_key",
  "memory",
  "space",
  "runtime",
  "cos_digest",
  "first_task",
]);

// DE-CONNECT: a DE connected to its agent container from CompanyOS's own records --------------

/** The connect flow's steps, in order (domain/provisioning/connect.ts). */
export const DeConnectStepName = z.enum([
  "seat",
  "agent_spec",
  "agent_key",
  "endpoint",
  "instance",
  "tool_grant",
  "bridge_unit",
]);
export const DeConnectStepState = z.enum(["done", "skipped", "waiting", "failed", "pending"]);

const PanelName = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9._-]{1,128}$/, "letters, digits, '.', '_' or '-'");
const McpToolName = z
  .string()
  .max(128)
  .regex(/^[A-Za-z0-9_][A-Za-z0-9_.:-]{0,127}$/, "a tool name");

/**
 * Start (or resume) a DE's connection. The placement defaults to what the DE's lead profile
 * records (`overrides.runtime`: container, profile) and then to the DE's earlier request; a new
 * endpoint needs `api_base_url` (the container's gateway origin). `adopt`: the DE already runs
 * (the backfill): no key is minted or replaced and its live spec is not rewritten.
 */
export const DeConnectRequest = z
  .strictObject({
    satellite_id: Uuid.optional(),
    instance_id: PanelName.optional(),
    profile_name: PanelName.refine((p) => !/^\.{1,2}$/.test(p), "not . or ..").optional(),
    api_base_url: z
      .string()
      .max(500)
      .regex(/^https?:\/\/[^?#@\s]+[^/]$/, "an http(s) origin, no trailing slash")
      .optional(),
    multiplex: z.boolean().optional(),
    adopt: z.boolean().optional(),
    /** The `companyos` MCP server's include list; null: every tool the key's scopes allow. */
    tools: z.array(McpToolName).max(500).nullable().optional(),
    /** Company connections to grant the DE (connections start closed: none unless named). */
    grants: z
      .array(z.strictObject({ connection_id: Uuid, level: z.enum(["use_read", "use_act"]) }))
      .max(50)
      .optional(),
  })
  .meta({ id: "DeConnectRequest" });

export const DeConnectView = z
  .object({
    satellite_id: Uuid,
    instance_id: z.string(),
    profile_name: z.string(),
    adopt: z.boolean(),
    connected_at: Timestamp.nullable(),
    steps: z.array(
      z.object({
        step: DeConnectStepName,
        state: DeConnectStepState,
        detail: z.string(),
        at: Timestamp.nullable(),
      }),
    ),
    spec_version: z.int().nullable(),
    endpoint_id: Uuid.nullable(),
    container_id: Uuid.nullable(),
    /** A gateway key CompanyOS minted is waiting for the panel's one hand-off. */
    gateway_key_waiting: z.boolean(),
  })
  .meta({ id: "DeConnectView" });

export const DeProvisioningView = z
  .object({
    membership_id: Uuid,
    origin: z.enum(["new", "existing"]),
    provisioned_at: Timestamp.nullable(),
    steps: z.array(
      z.object({
        step: DeProvisioningStepName,
        state: ProvisioningStepState.or(z.literal("pending")),
        detail: z.string(),
        at: Timestamp.nullable(),
      }),
    ),
    /** The satellite has taken the DE's agent key (it is no longer held here). */
    credentials_claimed: z.boolean(),
    /** Where the DE's bridge listens (null: none registered; it polls). Never the secret. */
    bridge: z
      .object({
        url: z.string(),
        container: z.string().nullable(),
        profile: z.string().nullable(),
        registered_at: Timestamp,
      })
      .nullable(),
    /** DE-CONNECT: its connection to its agent container (null: never asked). */
    connect: DeConnectView.nullable(),
  })
  .meta({ id: "DeProvisioningView" });

// The satellite's signed calls -------------------------------------------------------------------

/** A DE's credentials for the profile that runs it; the agent key is handed over once. */
export const DeCredentialsClaim = z
  .strictObject({
    company_id: Uuid,
    membership_id: Uuid,
    occurred_at: Timestamp,
  })
  .meta({ id: "DeCredentialsClaim" });

export const DeCredentials = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    /** The DE's CompanyOS MCP endpoint. */
    mcp_url: z.string(),
    /** The DE's MCP agent key, on the first claim only (null afterwards). */
    agent_key: z.string().nullable(),
    /** Its Recall agent key once Recall has issued it (null until then: claim again). */
    recall_agent_key: z.string().nullable(),
    recall_state: z.enum(["ready", "pending", "none"]),
  })
  .meta({ id: "DeCredentials" });

export const SystemAgentKeyClaim = z
  .strictObject({ company_id: Uuid, occurred_at: Timestamp })
  .meta({ id: "SystemAgentKeyClaim" });

export const SystemAgentKeyClaimed = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    /** `cosk_sa_…`, shown once; the earlier key is revoked. */
    key: z.string(),
    scopes: z.array(SystemAgentKeyScope),
  })
  .meta({ id: "SystemAgentKeyClaimed" });

// SATELLITE-RUNTIME: the satellite's runtime and its release reports ------------------------------

const NodeId = z.string().trim().min(1).max(128);

export const SatelliteRuntimeRequest = z
  .strictObject({ node_id: NodeId, occurred_at: Timestamp })
  .meta({ id: "SatelliteRuntimeRequest" });

/** One self-hosted DE the satellite's bridge runs (its keys are claimed separately). */
export const SatelliteRuntimeDe = z
  .object({
    /** The bridge's name for the DE (its key files, its wake path): the DE's membership id. */
    key: Uuid,
    membership_id: Uuid,
    name: z.string(),
    /** The container (endpoint `instance_id`) and Hermes profile that run it. */
    container: z.string(),
    profile: z.string(),
    /** The model its usage is booked to when Hermes does not name it; null: unknown. */
    model: z.string().nullable(),
    wake_path: z.string(),
  })
  .meta({ id: "SatelliteRuntimeDe" });

export const SatelliteRuntimeCompany = z
  .object({
    company_id: Uuid,
    api_url: z.string(),
    mcp_url: z.string(),
    settings: BridgeRuntimeSetting,
    /**
     * The company's line switches as they hold now (`line_switches` rules against its skills and
     * integrations); the panel writes them into the company's line config instead of
     * `line.local.yaml` edits.
     */
    switches: z.record(z.string(), z.boolean()),
    des: z.array(SatelliteRuntimeDe),
  })
  .meta({ id: "SatelliteRuntimeCompany" });

/**
 * OPERATOR-CONSOLE (§2.8): the ClawPanel baseline rendered from the operator settings — what a
 * satellite's containers are configured with, so the container file is an output of the rows
 * (never the other way round). `version` is the sha256 of the rendered content.
 */
export const RenderedBaseline = z
  .object({
    version: z.string().regex(/^[0-9a-f]{64}$/),
    /** The baseline version ClawPanel installs (null: the one it ships). */
    baseline_version: z.string().nullable(),
    profile: RuntimeProfileName,
    runtime: RuntimeProfile,
    containers: z.array(
      z.object({
        container: z.string(),
        baseline_version: z.string().nullable(),
        profile: RuntimeProfileName,
        runtime: RuntimeProfile,
      }),
    ),
    models: ModelsSetting,
    /** The base prompt version served with the wake (null: the one the code ships). */
    base_prompt_version: z.string().nullable(),
    skills: SkillsDefaultsSetting.shape.packs,
    wake: BridgeRuntimeSetting,
    /** Which setting versions it was rendered from (`key@scope` → version). */
    sources: z.record(z.string(), z.int()),
  })
  .meta({ id: "RenderedBaseline" });
export type RenderedBaseline = z.infer<typeof RenderedBaseline>;

/**
 * What a satellite runs, generated from CompanyOS (the registry, the DEs, the settings): the
 * panel writes it to the node as the bridge's only configuration and re-fetches it every
 * `config_poll_seconds` (`version` changes when anything in it does).
 */
export const SatelliteRuntime = z
  .object({
    /** OPERATOR-CONSOLE: the container baseline rendered from the operator settings. */
    baseline: RenderedBaseline.optional(),
    version: z.string().regex(/^[0-9a-f]{64}$/),
    generated_at: Timestamp,
    satellite: z.object({ id: Uuid, name: z.string(), node_id: z.string() }),
    /** Where the bridge listens for pushes (overlay host + the platform's port); null: poll only. */
    wake: z.object({ listen: z.string() }).nullable(),
    config_poll_seconds: z.int(),
    releases: z.object({ bridge: ReleasePin.nullable(), render_service: ReleasePin.nullable() }),
    companies: z.array(SatelliteRuntimeCompany),
  })
  .meta({ id: "SatelliteRuntime" });

/** The panel reports what it installed, rolled back or found when checking a satellite. */
export const SatelliteReleaseReport = z
  .strictObject({
    node_id: NodeId,
    component: SatelliteReleaseComponent,
    version: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[A-Za-z0-9._+-]+$/),
    sha256: z
      .string()
      .max(64)
      .regex(/^[0-9a-f]{64}$/)
      .nullable()
      .optional(),
    action: SatelliteReleaseAction,
    /** Paths or keys that differ from the release (empty: none). */
    drift: z.array(z.string().min(1).max(300)).max(100),
    detail: z.string().trim().max(300).nullable().optional(),
    occurred_at: Timestamp,
  })
  .meta({ id: "SatelliteReleaseReport" });

export const SatelliteIdleRequest = z
  .strictObject({
    node_id: NodeId,
    /** One container (endpoint `instance_id`); absent: every DE on the satellite. */
    container: z.string().trim().min(1).max(128).nullable().optional(),
    occurred_at: Timestamp,
  })
  .meta({ id: "SatelliteIdleRequest" });

/** Whether the satellite's (or one container's) DEs are idle, from CompanyOS's run state. */
export const SatelliteIdle = z
  .object({
    satellite_id: Uuid,
    container: z.string().nullable(),
    /** True when no DE below has a run queued or running. */
    idle: z.boolean(),
    des: z.array(
      z.object({
        company_id: Uuid,
        membership_id: Uuid,
        container: z.string(),
        profile: z.string(),
        running: z.int().min(0),
        queued: z.int().min(0),
      }),
    ),
    checked_at: Timestamp,
  })
  .meta({ id: "SatelliteIdle" });

export const SatelliteReleaseAck = z
  .object({ ok: z.literal(true), duplicate: z.boolean() })
  .meta({ id: "SatelliteReleaseAck" });

// DE-CONNECT: the gateway key a connect minted, and the bridge unit spec -----------------------

export const GatewayKeyClaim = z
  .strictObject({ company_id: Uuid, membership_id: Uuid, occurred_at: Timestamp })
  .meta({ id: "GatewayKeyClaim" });

/** The profile's `API_SERVER_KEY` that CompanyOS minted for a new endpoint (handed over once). */
export const GatewayKey = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    container: z.string(),
    profile: z.string(),
    /** The variable the profile's `.env` holds it in. */
    env: z.literal("API_SERVER_KEY"),
    key: z.string(),
  })
  .meta({ id: "GatewayKey" });

export const BridgeUnitRequest = z
  .strictObject({ node_id: NodeId, occurred_at: Timestamp })
  .meta({ id: "BridgeUnitRequest" });

const UnitFile = z.object({
  path: z.string(),
  /** Octal file mode, e.g. `0644`. */
  mode: z.string().regex(/^0[0-7]{3}$/),
  owner: z.string(),
  content: z.string(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});

/**
 * The satellite's wake-bridge unit and environment, generated from CompanyOS's rows (the
 * registry's endpoints, the companies' `bridge_runtime` settings, the platform's release pin):
 * ClawPanel writes the files verbatim and fills the listed key files from the claim calls. No
 * secret is in it. `version` changes when anything in it does (apply only when idle).
 */
export const BridgeUnitSpec = z
  .object({
    format: z.literal("companyos.bridge-unit/v1"),
    version: z.string().regex(/^[0-9a-f]{64}$/),
    generated_at: Timestamp,
    satellite: z.object({ id: Uuid, name: z.string(), node_id: z.string() }),
    unit: UnitFile.extend({ name: z.string() }),
    env: UnitFile,
    /** The roster file the unit reads: written from `POST /v1/aigentpanel/satellite-runtime`. */
    config: z.object({
      path: z.string(),
      mode: z.string(),
      from: z.literal("aigentpanel.satelliteRuntime"),
      runtime_version: z.string().regex(/^[0-9a-f]{64}$/),
    }),
    stop: z.object({ kill_mode: z.literal("mixed"), timeout_stop_seconds: z.int().min(1) }),
    /** One 0700 directory per company under the bridge root. */
    directories: z.array(z.object({ path: z.string(), mode: z.string(), owner: z.string() })),
    /** The key files the bridge reads, and where their values come from (never the value). */
    credentials: z.array(
      z.object({
        company_id: Uuid,
        membership_id: Uuid,
        path: z.string(),
        mode: z.string(),
        /**
         * `aigentpanel.deCredentials`: claim it (its `field`); `panel`: the panel makes it and
         * registers it with the `bridge.registered` callback (the push secret).
         */
        from: z.enum(["aigentpanel.deCredentials", "panel"]),
        field: z.string().nullable(),
        /** CompanyOS still holds the value for its one hand-off (claim it now). */
        waiting: z.boolean(),
      }),
    ),
    /**
     * Recall memory: one key per DE (never per helper), written into the DE's profile `.env`
     * from its de-credentials claim (`recall_agent_key`).
     */
    memory: z.array(
      z.object({
        company_id: Uuid,
        membership_id: Uuid,
        container: z.string(),
        profile: z.string(),
        env: z.literal("RECALL_AGENT_KEY"),
        from: z.literal("aigentpanel.deCredentials"),
        field: z.literal("recall_agent_key"),
      }),
    ),
    /**
     * Helpers (AGs) have no memory: no Recall key, no read access (not the calling DE's, not a
     * person's), and they never save any. Everything a helper needs comes in the calling DE's
     * brief. Only DEs (and people) have memory identities.
     */
    helper_memory: z.object({
      own_key: z.literal(false),
      access: z.literal("none"),
      saves: z.literal(false),
      context: z.literal("brief"),
    }),
    /** Profiles whose `API_SERVER_KEY` CompanyOS minted and still holds for the panel. */
    gateway_keys: z.array(
      z.object({
        company_id: Uuid,
        membership_id: Uuid,
        container: z.string(),
        profile: z.string(),
        from: z.literal("aigentpanel.gatewayKey"),
      }),
    ),
  })
  .meta({ id: "BridgeUnitSpec" });

// Routes -----------------------------------------------------------------------------------------

export const provisioningRoutes = {
  "operator.provisioning.settings.get": defineRoute({
    ...operator,
    id: "operator.provisioning.settings.get",
    method: "get",
    path: `${op}/provisioning/settings`,
    summary:
      "The provisioning settings: shipped default, platform default, a company's override, what applies (super admins only)",
    query: ProvisioningSettingsQuery,
    response: { status: 200, schema: ProvisioningSettingList },
    errors: ["forbidden"],
    emits: [],
  }),
  "operator.provisioning.settings.put": defineRoute({
    ...operator,
    id: "operator.provisioning.settings.put",
    method: "put",
    path: `${op}/provisioning/settings/{key}`,
    summary:
      "Store or clear one provisioning setting, platform-wide or for one company (super admins only; audited)",
    params: ProvisioningSettingParams,
    body: ProvisioningSettingPut,
    // A control-plane store and its `platform_audit` row; no company state, so no company event.
    safe: true,
    response: { status: 200, schema: ProvisioningSettingView },
    errors: ["forbidden", "not_found"],
    emits: [],
  }),
  "operator.companies.provision": defineRoute({
    ...operator,
    id: "operator.companies.provision",
    method: "post",
    // Not under `/companies/{company_id}/…`: a platform super admin's call, not a company grant's.
    path: `${op}/provisioning/companies/{company_id}`,
    summary:
      "Preview or queue a company's own provisioning: its system agent and its feature rules (super admins only)",
    params: ProvisioningCompanyParams,
    body: CompanyProvisionRequest,
    // The request is a control-plane flag and an audit row; the sweep does the company's writes.
    safe: true,
    response: { status: 200, schema: CompanyProvisionView },
    errors: ["forbidden", "not_found"],
    emits: [],
  }),
  "digitalEmployees.provisioning.get": defineRoute({
    ...company,
    id: "digitalEmployees.provisioning.get",
    method: "get",
    path: "/v1/companies/{co}/digital-employees/{id}/provisioning",
    summary: "A DE's set-up: each step, whether the satellite has its keys, its bridge",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: DeProvisioningView },
    errors: ["not_found"],
    emits: [],
  }),
  "digitalEmployees.provision": defineRoute({
    ...company,
    id: "digitalEmployees.provision",
    method: "post",
    path: "/v1/companies/{co}/digital-employees/{id}/provisioning",
    summary:
      "Run a DE's set-up again (idempotent: only what is missing is done; the worker runs it)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 202, schema: DeProvisioningView },
    errors: ["not_found", "conflict"],
    emits: ["digital_employee.provision_requested"],
  }),
  "digitalEmployees.connect": defineRoute({
    ...company,
    id: "digitalEmployees.connect",
    method: "post",
    path: "/v1/companies/{co}/digital-employees/{id}/connect",
    summary:
      "Connect a DE to its agent container from CompanyOS's records, or resume it (idempotent; the worker runs the steps)",
    access: "manager",
    params: CompanyItemParams,
    body: DeConnectRequest,
    response: { status: 202, schema: DeProvisioningView },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: ["digital_employee.connect_requested"],
  }),
  "aigentpanel.gatewayKey": defineRoute({
    ...panel,
    id: "aigentpanel.gatewayKey",
    method: "post",
    path: "/v1/aigentpanel/gateway-key",
    summary:
      "The satellite claims the API_SERVER_KEY CompanyOS minted for a DE's new profile (HMAC; once)",
    body: GatewayKeyClaim,
    // A stamp on the DE's connect row and an audit line; the key itself is not company state.
    safe: true,
    response: { status: 200, schema: GatewayKey },
    errors: ["signature_invalid", "not_found", "conflict"],
    emits: [],
  }),
  "aigentpanel.bridgeUnit": defineRoute({
    ...panel,
    id: "aigentpanel.bridgeUnit",
    method: "post",
    path: "/v1/aigentpanel/bridge-unit",
    summary:
      "The satellite's wake-bridge systemd unit and env, generated from the registry and settings, with the key files to fill (HMAC; read only)",
    body: BridgeUnitRequest,
    // A read: the POST only carries the signed body.
    safe: true,
    response: { status: 200, schema: BridgeUnitSpec },
    errors: ["signature_invalid", "not_found"],
    emits: [],
  }),
  "aigentpanel.deCredentials": defineRoute({
    ...panel,
    id: "aigentpanel.deCredentials",
    method: "post",
    path: "/v1/aigentpanel/de-credentials",
    summary:
      "The satellite claims a DE's MCP and Recall keys for the profile that runs it (HMAC; the agent key once)",
    body: DeCredentialsClaim,
    response: { status: 200, schema: DeCredentials },
    errors: ["signature_invalid", "not_found", "conflict"],
    emits: ["digital_employee.credentials_claimed"],
  }),
  "aigentpanel.systemAgentKey": defineRoute({
    ...panel,
    id: "aigentpanel.systemAgentKey",
    method: "post",
    path: "/v1/aigentpanel/system-agent-key",
    summary:
      "The satellite claims a fresh key for the company's system agent (HMAC; revokes the earlier one)",
    body: SystemAgentKeyClaim,
    // The operator-only key store and an audit row (as the super admin's key issue).
    safe: true,
    response: { status: 201, schema: SystemAgentKeyClaimed },
    errors: ["signature_invalid", "not_found"],
    emits: [],
  }),
  "aigentpanel.satelliteRuntime": defineRoute({
    ...panel,
    id: "aigentpanel.satelliteRuntime",
    method: "post",
    path: "/v1/aigentpanel/satellite-runtime",
    summary:
      "The satellite's runtime: the self-hosted DEs its bridge runs, their settings, the wake listener and the pinned releases (HMAC; read only)",
    body: SatelliteRuntimeRequest,
    // A read: the POST only carries the signed body.
    safe: true,
    response: { status: 200, schema: SatelliteRuntime },
    errors: ["signature_invalid", "not_found"],
    emits: [],
  }),
  "aigentpanel.satelliteIdle": defineRoute({
    ...panel,
    id: "aigentpanel.satelliteIdle",
    method: "post",
    path: "/v1/aigentpanel/satellite-idle",
    summary:
      "Whether a satellite's (or one container's) DEs are idle — no run queued or running — from CompanyOS's registry and run state (HMAC; read only)",
    body: SatelliteIdleRequest,
    // A read: the POST only carries the signed body.
    safe: true,
    response: { status: 200, schema: SatelliteIdle },
    errors: ["signature_invalid", "not_found"],
    emits: [],
  }),
  "aigentpanel.releaseReport": defineRoute({
    ...panel,
    id: "aigentpanel.releaseReport",
    method: "post",
    path: "/v1/aigentpanel/release-report",
    summary:
      "The panel reports a release it installed, rolled back or checked on a satellite, with any drift (HMAC; audited on install and rollback)",
    body: SatelliteReleaseReport,
    // A control-plane row and its audit line; no company state.
    safe: true,
    response: { status: 200, schema: SatelliteReleaseAck },
    errors: ["signature_invalid", "not_found"],
    emits: [],
  }),
} as const;
